'use client';

import React, { useMemo, useState } from 'react';
import StickyBox from 'react-sticky-box';
import { useTranslation } from 'react-i18next';
import type { EChartsOption } from 'echarts';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { Icon, SectionTitle } from '@benzinga/core-ui';
import {
  getCapitalRaises,
  getInvestorsCompanyName,
  getInvestorsList,
  getKeyPeople,
  getPrivateCompanyStats,
  type InvestorsResponse,
  type NormalizedPrivateMarketPrice,
  type PrivateCompanyCapitalRaise,
  type PrivateCompanyInvestor,
  type PrivateCompanyPerson,
} from '@benzinga/investors-manager';
import type { BaseQuotePageProps } from '../../utils/quote/types';
import FAQs from '../FAQs';
import { QuoteAboutCard } from './QuoteAboutCard';
import { QuoteFooter } from './QuoteFooter';
import { ChartPlotSkeleton } from './QuoteSkeletons';

const ReactECharts = React.lazy(() => import('echarts-for-react'));

const RaptiveAdPlaceholder = React.lazy(() =>
  import('@benzinga/ads').then(module => ({
    default: module.RaptiveAdPlaceholder,
  })),
);

interface PrivateCompanyOverviewProps extends BaseQuotePageProps {
  investors: InvestorsResponse | null;
}

const INITIAL_VISIBLE_ROWS = 4;
const CHART_UP = '#15803d';
const CHART_DOWN = '#dc2626';

const formatDisplayDate = (value: string | undefined): string => {
  if (!value || value === '—') return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const formatAxisDate = (timestamp: number): string =>
  new Date(timestamp).toLocaleDateString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });

export const getPrivateMarketPricingTone = (
  points: NormalizedPrivateMarketPrice[],
): { change: number; changePercent: number; color: string } => {
  const first = points[0];
  const latest = points[points.length - 1];
  if (!first || !latest) {
    return { change: 0, changePercent: 0, color: CHART_UP };
  }
  const change = latest.price - first.price;
  const changePercent = first.price ? (change / first.price) * 100 : 0;
  return {
    change,
    changePercent,
    color: change >= 0 ? CHART_UP : CHART_DOWN,
  };
};

const formatInvestorName = (investor: PrivateCompanyInvestor): string | null => {
  const name = investor.name;
  return typeof name === 'string' && name.trim() ? name.trim() : null;
};

const joinReadableList = (items: string[]): string => {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const getStatValue = (stats: ReturnType<typeof getPrivateCompanyStats>, label: string): string | undefined => {
  const value = stats.find(stat => stat.label === label)?.value;
  return value && value !== '—' ? value : undefined;
};

const buildPrivateCompanyFaqs = ({
  capitalRaiseRows,
  companyName,
  investorRows,
  keyPeopleRows,
  pricingPoints,
  stats,
  symbol,
}: {
  capitalRaiseRows: PrivateCompanyCapitalRaise[];
  companyName: string;
  investorRows: PrivateCompanyInvestor[];
  keyPeopleRows: PrivateCompanyPerson[];
  pricingPoints: NormalizedPrivateMarketPrice[];
  stats: ReturnType<typeof getPrivateCompanyStats>;
  symbol: string;
}) => {
  const sector = getStatValue(stats, 'Sector');
  const founded = getStatValue(stats, 'Founded');
  const headquarters = getStatValue(stats, 'Headquarters');
  const amountRaised = getStatValue(stats, 'Amount Raised');
  const latestPrice = pricingPoints[pricingPoints.length - 1];

  const aboutDetails = [
    sector ? `operates in the ${sector} sector` : null,
    founded ? `was founded in ${founded}` : null,
    headquarters ? `is headquartered in ${headquarters}` : null,
  ].filter((detail): detail is string => Boolean(detail));

  const aboutLead =
    aboutDetails.length > 0
      ? `${companyName} (${symbol}) is a private company tracked on Nasdaq Private Market. It ${joinReadableList(aboutDetails)}.`
      : `${companyName} (${symbol}) is a private company tracked on Nasdaq Private Market.`;

  const aboutAnswer = (
    <div className="flex flex-col gap-2">
      <p>{aboutLead}</p>
      {latestPrice ? (
        <p>
          The latest Nasdaq Private Market evaluated price is ${latestPrice.price.toFixed(2)} as of{' '}
          {formatDisplayDate(latestPrice.date)}.
        </p>
      ) : null}
    </div>
  );

  const investorNames = investorRows.map(formatInvestorName).filter((name): name is string => Boolean(name));
  const visibleInvestors = investorNames.slice(0, 8);
  const remainingInvestors = investorNames.length - visibleInvestors.length;
  const investorsAnswer =
    investorNames.length > 0 ? (
      <p>
        Notable investors in {companyName} include {joinReadableList(visibleInvestors)}
        {remainingInvestors > 0 ? `, along with ${remainingInvestors} more` : ''}.
      </p>
    ) : (
      <p>Investor details are not available for {companyName} yet.</p>
    );

  const sortedRaises = [...capitalRaiseRows].sort((left, right) => {
    const leftTime = left.date && left.date !== '—' ? Date.parse(left.date) : 0;
    const rightTime = right.date && right.date !== '—' ? Date.parse(right.date) : 0;
    return rightTime - leftTime;
  });
  const latestRaise = sortedRaises[0];
  const capitalAnswer =
    sortedRaises.length > 0 && latestRaise ? (
      <div className="flex flex-col gap-2">
        <p>
          {companyName} has {sortedRaises.length} reported capital raise
          {sortedRaises.length === 1 ? '' : 's'}
          {amountRaised ? `, totaling ${amountRaised}` : ''}.
        </p>
        <p>
          The most recent round is {latestRaise.series || 'an undisclosed round'}
          {latestRaise.date && latestRaise.date !== '—' ? ` on ${formatDisplayDate(latestRaise.date)}` : ''}
          {latestRaise.amount && latestRaise.amount !== '—' ? ` for ${latestRaise.amount}` : ''}.
        </p>
      </div>
    ) : (
      <p>Capital raise history is not available for {companyName} yet.</p>
    );

  const peopleNames = keyPeopleRows
    .slice(0, 5)
    .map(person => {
      if (!person.name || person.name === '—') return null;
      const title = person.title && person.title !== '—' ? person.title : null;
      return title ? `${person.name} (${title})` : person.name;
    })
    .filter((name): name is string => Boolean(name));
  const peopleAnswer =
    peopleNames.length > 0 ? (
      <p>
        Key people at {companyName} include {joinReadableList(peopleNames)}
        {keyPeopleRows.length > peopleNames.length ? `, and ${keyPeopleRows.length - peopleNames.length} more` : ''}.
      </p>
    ) : (
      <p>Leadership details are not available for {companyName} yet.</p>
    );

  return [
    {
      answer: aboutAnswer,
      question: `What is ${companyName} (${symbol})?`,
    },
    {
      answer: investorsAnswer,
      question: `Who are the investors in ${companyName}?`,
    },
    {
      answer: capitalAnswer,
      question: `Has ${companyName} raised capital?`,
    },
    {
      answer: peopleAnswer,
      question: `Who are the key people at ${companyName}?`,
    },
    {
      answer: (
        <p>
          Company profile, investor, capital raise, and pricing information on this page is sourced from Nasdaq Private
          Market. Figures may update less frequently than public equity data.
        </p>
      ),
      question: 'Where does this data come from?',
    },
  ];
};

const PrivateCompanyMetrics = ({ stats }: { stats: ReturnType<typeof getPrivateCompanyStats> }) => {
  const { t } = useTranslation('quote');

  return (
    <div className="card p-4">
      <SectionTitle className="mb-2" level={2}>
        {t('Quote.Metrics.key-statistics')}
      </SectionTitle>
      {stats.map(stat => (
        <div
          className="flex w-full items-center justify-between border-b border-bzblue-300 py-1.5 text-sm last:border-b-0"
          key={stat.label}
        >
          <div className="label text-gray-500">{stat.label}</div>
          <div className="value text-right font-bold">{stat.value}</div>
        </div>
      ))}
    </div>
  );
};

export const getCeoName = (people: PrivateCompanyPerson[]): string | undefined => {
  const ceo = people.find(person => {
    const title = (person.title ?? '').toLowerCase();
    return /\bceo\b|chief executive/.test(title);
  });
  return ceo?.name && ceo.name !== '—' ? ceo.name : undefined;
};

const PrivateMarketPricingChart = ({
  companyName,
  points,
  symbol,
}: {
  companyName: string;
  points: NormalizedPrivateMarketPrice[];
  symbol: string;
}) => {
  const option = useMemo((): EChartsOption | null => {
    if (points.length < 2) return null;

    const { color } = getPrivateMarketPricingTone(points);
    const labels = points.map(point => formatAxisDate(point.timestamp));
    const prices = points.map(point => point.price);
    const labelStep = Math.max(1, Math.ceil(points.length / 6));
    const areaFill =
      color === CHART_UP
        ? {
            color: {
              type: 'linear' as const,
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(21,128,61,0.22)' },
                { offset: 1, color: 'rgba(21,128,61,0)' },
              ],
            },
          }
        : {
            color: {
              type: 'linear' as const,
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(220,38,38,0.22)' },
                { offset: 1, color: 'rgba(220,38,38,0)' },
              ],
            },
          };

    return {
      grid: { bottom: 24, left: 44, right: 16, top: 8 },
      series: [
        {
          areaStyle: areaFill,
          data: prices,
          itemStyle: { color },
          lineStyle: { color, width: 2 },
          name: 'Price',
          showSymbol: false,
          smooth: 0.2,
          type: 'line',
        },
      ],
      tooltip: {
        formatter: (params: unknown) => {
          const rows = Array.isArray(params) ? params : [params];
          const first = rows[0] as { dataIndex?: number } | undefined;
          const dataIndex = first?.dataIndex ?? 0;
          const point = points[dataIndex];
          if (!point) return '';
          return `<b>${formatDisplayDate(point.date)}</b><br/>$${point.price.toFixed(2)}`;
        },
        trigger: 'axis',
      },
      xAxis: {
        axisLabel: {
          color: '#6b7280',
          fontSize: 11,
          interval: (_index: number) => _index % labelStep === 0 || _index === points.length - 1,
        },
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisTick: { show: false },
        boundaryGap: false,
        data: labels,
        type: 'category',
      },
      yAxis: {
        axisLabel: {
          color: '#6b7280',
          fontSize: 11,
          formatter: (value: number) => `$${value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)}`,
        },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
        type: 'value',
        scale: true,
      },
    };
  }, [points]);

  if (points.length < 2 || !option) {
    return (
      <div className="card flex h-[220px] flex-col items-center justify-center bg-white !p-6 text-center md:h-[280px]">
        <SectionTitle level={2} size="2xl" uppercase={false}>
          Private market pricing
        </SectionTitle>
        <p className="mt-2 max-w-md text-sm text-grey-darker">
          {points.length === 1
            ? `Latest Nasdaq Private Market price for ${companyName} (${symbol}): $${points[0].price.toFixed(2)}.`
            : `No Nasdaq Private Market evaluated price is published for ${companyName} (${symbol}) yet. Chart history will appear when pricing is available.`}
        </p>
      </div>
    );
  }

  const latest = points[points.length - 1];
  const { change, changePercent, color } = getPrivateMarketPricingTone(points);
  const changeSign = change > 0 ? '+' : '';

  return (
    <div
      aria-label={`${companyName} private market pricing chart`}
      className="card flex flex-col gap-4 bg-white !p-4 md:!p-6"
      role="img"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <div className="text-xs font-medium uppercase tracking-wide text-grey-darker">Private market pricing</div>
          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5">
            <span className="text-3xl font-semibold leading-none tracking-tight text-bzgray-900 mr-2">
              ${latest.price.toFixed(2)}
            </span>
            <span className="text-sm font-semibold" style={{ color }}>
              {changeSign}
              {change.toFixed(2)} ({changeSign}
              {changePercent.toFixed(1)}%)
            </span>
          </div>
        </div>
        <div className="pt-0.5 text-right text-xs text-grey-darker">As of {formatDisplayDate(latest.date)}</div>
      </div>
      <React.Suspense fallback={<ChartPlotSkeleton label="Loading private market pricing chart" />}>
        <ReactECharts notMerge option={option} style={{ height: 220, width: '100%' }} />
      </React.Suspense>
    </div>
  );
};

const PrivateCompanyCardList = <T,>({
  columns,
  emptyText,
  renderRow,
  rowKey,
  rows,
  title,
}: {
  columns: string[];
  emptyText: string;
  renderRow: (row: T) => React.ReactNode;
  rowKey: (row: T, index: number) => string;
  rows: T[];
  title: string;
}) => {
  const [expanded, setExpanded] = useState(false);
  const visibleRows = expanded ? rows : rows.slice(0, INITIAL_VISIBLE_ROWS);
  const hasMore = rows.length > INITIAL_VISIBLE_ROWS;
  const columnCount = Math.max(columns.length, 1);

  return (
    <div className="overflow-hidden rounded-lg border border-bzblue-300 bg-bzblue-100">
      <div className="px-3 pt-3 pb-2">
        <SectionTitle className="!mb-0 text-bzblue-800" level={2} size="xl">
          {title}
        </SectionTitle>
      </div>
      {!rows.length ? (
        <p className="px-3 pb-3 text-sm text-grey-darker">{emptyText}</p>
      ) : (
        <>
          {columns.length > 0 ? (
            <div
              className="grid gap-2 px-3 pb-1 text-xs font-medium text-bzblue-500"
              style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
            >
              {columns.map(column => (
                <div key={column}>{column}</div>
              ))}
            </div>
          ) : null}
          <div className="flex flex-col gap-1.5 px-3 pb-2">
            {visibleRows.map((row, index) => (
              <div
                className="grid items-center gap-2 rounded-md bg-white px-3 py-2 text-sm text-bzgray-900"
                key={rowKey(row, index)}
                style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
              >
                {renderRow(row)}
              </div>
            ))}
          </div>
          {hasMore ? (
            <button
              className="flex w-full items-center justify-center gap-1 border-t border-bzblue-300 py-2 text-sm font-medium text-blue-600 hover:bg-bzblue-200"
              onClick={() => setExpanded(current => !current)}
              type="button"
            >
              {expanded ? 'See Less' : 'See More'}
              <Icon icon={expanded ? faChevronUp : faChevronDown} />
            </button>
          ) : null}
        </>
      )}
    </div>
  );
};

export const PrivateCompanyOverview: React.FC<PrivateCompanyOverviewProps> = ({ investors, profile, symbol }) => {
  const { t } = useTranslation('quote');
  const companyName = profile.privateCompanyName || getInvestorsCompanyName(investors, symbol);
  const investorRows = getInvestorsList(investors);
  const capitalRaiseRows = getCapitalRaises(investors);
  const keyPeopleRows = getKeyPeople(investors);
  const stats = getPrivateCompanyStats(investors);
  const pricingPoints = investors?.pricing ?? [];
  const aboutDescription =
    typeof investors?.description === 'string' && investors.description.trim()
      ? investors.description.trim()
      : undefined;
  const website =
    (typeof investors?.company?.marketingUrl === 'string' && investors.company.marketingUrl.trim()) || undefined;
  const industry = getStatValue(stats, 'Sector');
  const founded = getStatValue(stats, 'Founded');
  const ceo = getCeoName(keyPeopleRows);
  const aboutMetaItems = [
    industry ? { label: t('Quote.About.industry'), value: industry } : null,
    website
      ? {
          href: website.startsWith('http') ? website : `https://${website}`,
          label: 'Website',
          value: 'See more',
        }
      : null,
    ceo ? { label: 'CEO', value: ceo } : null,
    founded ? { label: 'Founded', value: founded } : null,
  ].filter((item): item is { href?: string; label: string; value: string } => Boolean(item));
  const aboutCard = (
    <QuoteAboutCard companyName={companyName} description={aboutDescription} metaItems={aboutMetaItems} />
  );

  return (
    <>
      <div className="main-div flex w-full gap-4 px-4">
        <div className="top-area-and-sidebar-container mx-auto flex w-full max-w-[1400px] gap-4">
          <div className="flex w-full flex-col gap-6 p-0 md:gap-4 lg:w-8/12">
            <PrivateMarketPricingChart companyName={companyName} points={pricingPoints} symbol={symbol} />

            <div className="metrics-mobile flex w-full flex-col gap-4 lg:hidden">
              <PrivateCompanyMetrics stats={stats} />
              {aboutCard}
            </div>

            <PrivateCompanyCardList
              columns={['Round', 'Date', 'Total']}
              emptyText="Capital raise history will appear here when available."
              renderRow={(row: PrivateCompanyCapitalRaise) => (
                <>
                  <div className="font-semibold">{row.series || '—'}</div>
                  <div>{formatDisplayDate(row.date)}</div>
                  <div className="font-semibold">
                    {row.amount == null || row.amount === '' ? '—' : String(row.amount)}
                  </div>
                </>
              )}
              rowKey={(row, index) => `${row.series}-${row.date}-${row.amount ?? ''}-${index}`}
              rows={capitalRaiseRows}
              title="Capital Raises"
            />

            <div className="flex flex-col gap-4 md:flex-row">
              <div className="w-full md:w-7/12">
                <PrivateCompanyCardList
                  columns={['Title', 'Name']}
                  emptyText="Key people will appear here when available."
                  renderRow={(row: PrivateCompanyPerson) => (
                    <>
                      <div className="font-semibold leading-snug">{row.title || '—'}</div>
                      <div className="font-semibold">{row.name || '—'}</div>
                    </>
                  )}
                  rowKey={(row, index) => `${row.name}-${row.title}-${index}`}
                  rows={keyPeopleRows}
                  title="Key People"
                />
              </div>
              <div className="w-full md:w-5/12">
                <PrivateCompanyCardList
                  columns={[]}
                  emptyText="No investors listed for this company yet."
                  renderRow={(row: PrivateCompanyInvestor) => (
                    <div className="font-semibold">{row.name || '—'}</div>
                  )}
                  rowKey={(row, index) => `${row.name || 'investor'}-${index}`}
                  rows={investorRows}
                  title="Investors"
                />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <SectionTitle level={2} size="2xl" uppercase={false}>
                {t('Quote.FAQ.faq')}
              </SectionTitle>
              <FAQs
                FAQsDataSet={buildPrivateCompanyFaqs({
                  capitalRaiseRows,
                  companyName,
                  investorRows,
                  keyPeopleRows,
                  pricingPoints,
                  stats,
                  symbol,
                })}
                title={companyName}
              />
            </div>
          </div>

          <div className="hidden w-full flex-col items-center gap-4 lg:flex lg:w-4/12">
            <div className="w-full">
              <PrivateCompanyMetrics stats={stats} />
            </div>
            <div className="w-full">{aboutCard}</div>
            <div className="card w-full p-4 text-sm text-grey-darker">
              Nasdaq Private Market companies are not listed on public exchanges. Pricing and fundraising figures may
              update less frequently than public equities.
            </div>
            <StickyBox className="z-[1]" offsetTop={290} offsetBottom={0}>
              <div className="sidebar-ad-container flex flex-col gap-4">
                <React.Suspense fallback={<div />}>
                  <RaptiveAdPlaceholder
                    className="w-[300px] overflow-hidden"
                    disableMargins={true}
                    onlyDesktop={true}
                    type="static-sidebar"
                  />
                </React.Suspense>
              </div>
            </StickyBox>
          </div>
        </div>
      </div>
      <QuoteFooter activeTab="overview" showBrowseLink={false} />
    </>
  );
};
